// IMPORTANT: host-redirect MUST be the first import. It rewrites the URL
// off any *.run.app host onto designer.samsurveyor.com before anything
// touches Hanko (Hanko's cookie is scoped to .samsurveyor.com and won't
// ride on run.app). See src/host-redirect.js.
import './host-redirect';
import './quietConsole';
import './styles/design-tokens.css';
import '@cloudsam/ui/dist/styles.css'; // shared shell + components; must follow the tokens
import './styles/app-base.css';
import './hanko-init'; // must be imported before any auth-aware component
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import IdleSessionGate from './components/IdleSessionGate';
import { getSurveySummaryFromCloud, getSurveyVersionFromCloud } from './utils/cloudApi';
import { reconstructPayloadFromDescriptor } from './utils/payloadRecovery';

const rootEl = document.getElementById('root');
// Surface identity (surface-identity-and-shell.md §1): the tint is declared once
// on the app root and inherited by every panel via --color-surface-*. Set on the
// #root element rather than a JSX wrapper so portalled overlays that mount inside
// #root inherit it too.
rootEl.classList.add('app--designer');
const root = ReactDOM.createRoot(rootEl);

async function bootstrapDeepLink() {
  try {
    const params = new URLSearchParams(window.location.search);
    const surveyId = params.get('surveyId');
    const ver = params.get('ver');
    if (!surveyId) return null;
    let versionNumber = null;
    if (!ver || ver === 'latest') {
      const summary = await getSurveySummaryFromCloud(surveyId);
      // API returns version directly on the survey object, not nested in latestVersion
      versionNumber = summary?.version || 1;
    } else {
      versionNumber = Number(ver);
    }
    const versionResp = await getSurveyVersionFromCloud(surveyId, versionNumber);
    // sam-dbam returns {data: {data: {...}, meta: {...}}, etag: ...}
    // The inner data.data contains the actual version object with designer_payload_json
    const versionData = versionResp?.data?.data || versionResp?.data || {};
    let payload = versionData.designer_payload_json;
    if (!payload || typeof payload !== 'object') {
      // Legacy survey: designer_payload_json was not persisted.
      // Reconstruct a usable payload from descriptor_json so the survey can still be opened.
      console.warn('designer_payload_json missing — reconstructing from descriptor_json');
      payload = reconstructPayloadFromDescriptor(versionData.descriptor_json);
      if (!payload) {
        throw new Error('designer_payload_json missing and descriptor_json cannot be reconstructed');
      }
    }
    // Return both payload and survey context for proper state restoration
    return {
      payload,
      surveyContext: {
        surveyId,
        versionNumber,
        etag: versionResp?.etag || null
      }
    };
  } catch (e) {
    console.error('Deep-link load failed:', e.message);
    return null;
  }
}

(async () => {
  // Ensure Designer tab is named for reuse via window.open(name)
  try { window.name = 'CloudSamDesigner'; } catch (e) { }
  try { document.title = 'Sam Surveyor Designer'; } catch (e) { }

  // Deep link: if surveyId is in URL, always try to load it (even in local mode)
  // This allows Console's Edit button to work regardless of storage configuration
  const params = new URLSearchParams(window.location.search);
  const hasSurveyIdInUrl = !!params.get('surveyId');

  const bootstrapResult = hasSurveyIdInUrl
    ? await bootstrapDeepLink()
    : null;
  const payload = bootstrapResult?.payload || null;
  const surveyContext = bootstrapResult?.surveyContext || null;

  root.render(
    <AuthProvider>
      <ProtectedRoute>
        {/* Inside ProtectedRoute, so the idle clock only runs for somebody
            who is actually signed in. App registers its draft rescue with
            the session this gate provides. */}
        <IdleSessionGate>
          <App initialPayload={payload} initialSurveyContext={surveyContext} />
        </IdleSessionGate>
      </ProtectedRoute>
    </AuthProvider>
  );
})();